import { Link } from 'react-router-dom'
import LegalLayout from '@/components/LegalLayout'
import Seo from '@/components/Seo'

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Use"
      tagline="Please read these terms carefully before using Pelvia"
      effectiveDate="January 24, 2026"
    >
      <Seo
        title="Terms of Use | Pelvia"
        description="The rules and restrictions that govern your use of Pelvia's website, products, and services."
        path="/terms"
      />
      <p>
        Welcome to Pelvia. Please read on to learn the rules and restrictions that govern your use
        of our website(s), products, services and applications (the "Services"). If you have any
        questions, comments, or concerns regarding these terms or the Services, please contact us
        at:
      </p>

      <div className="contact-info">
        <p>
          <strong>Email:</strong> <a href="mailto:support@pelvia.me">support@pelvia.me</a>
        </p>
      </div>

      <p>
        These Terms of Use (the "Terms") are a binding contract between you and PELVIA HEALTH,
        INC. ("Pelvia," "we" and "us"). Your use of the Services in any way means that you agree
        to all of these Terms, and these Terms will remain in effect while you use the Services.
        These Terms include the provisions in this document as well as those in the{' '}
        <Link to="/privacy">Privacy Policy</Link>. Your use of or participation in certain
        Services may also be subject to additional policies, rules and/or conditions ("Additional
        Terms"), which are incorporated herein by reference, and you understand and agree that by
        using or participating in any such Services, you agree to also comply with these
        Additional Terms.
      </p>

      <p>
        Please read these Terms carefully. They cover important information about Services
        provided to you. These Terms include information about future changes to these Terms,
        limitations of liability, a class action waiver and resolution of disputes by arbitration
        instead of in court.{' '}
        <strong>
          PLEASE NOTE THAT YOUR USE OF AND ACCESS TO OUR SERVICES ARE SUBJECT TO THE FOLLOWING
          TERMS; IF YOU DO NOT AGREE TO ALL OF THE FOLLOWING, YOU MAY NOT USE OR ACCESS THE
          SERVICES IN ANY MANNER.
        </strong>
      </p>

      <div className="notice-box critical">
        <strong>ARBITRATION NOTICE AND CLASS ACTION WAIVER:</strong>
        EXCEPT FOR CERTAIN TYPES OF DISPUTES DESCRIBED IN THE ARBITRATION AGREEMENT SECTION BELOW,
        YOU AGREE THAT DISPUTES BETWEEN YOU AND US WILL BE RESOLVED BY BINDING, INDIVIDUAL
        ARBITRATION AND YOU WAIVE YOUR RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR
        CLASS-WIDE ARBITRATION.
      </div>

      <h2>Will these Terms ever change?</h2>
      <p>
        We are constantly trying to improve our Services, so these Terms may need to change along
        with our Services. We reserve the right to change the Terms at any time, but if we do, we
        will place a notice on our site located at https://pelvia.me/, send you an email, and/or
        notify you by some other means.
      </p>

      <p>
        If you don't agree with the new Terms, you are free to reject them; unfortunately, that
        means you will no longer be able to use the Services. If you use the Services in any way
        after a change to the Terms is effective, that means you agree to all of the changes.
      </p>

      <p>
        Except for changes by us as described here, no other amendment or modification of these
        Terms will be effective unless in writing and signed by both you and us.
      </p>

      <h2>What about my privacy?</h2>
      <p>
        Pelvia takes the privacy of its users very seriously. For the current Pelvia Privacy
        Policy, please click <Link to="/privacy">here</Link>.
      </p>

      <h2>Children's Online Privacy Protection Act</h2>
      <p>
        The Children's Online Privacy Protection Act ("COPPA") requires that online service
        providers obtain parental consent before they knowingly collect personally identifiable
        information online from children who are under 13 years of age. We do not knowingly
        collect or solicit personally identifiable information from children under 16 years of
        age; if you are a child under 16 years of age, please do not attempt to register for or
        otherwise use the Services or send us any personal information. If we learn we have
        collected personal information from a child under 16 years of age, we will delete that
        information as quickly as possible. If you believe that a child under 16 years of age may
        have provided us personal information, please contact us at{' '}
        <a href="mailto:support@pelvia.me">support@pelvia.me</a>.
      </p>

      <h2>Health Insurance Portability & Accountability Act</h2>
      <p>
        Some professionals you may interact with in connection with our Services qualify as
        "health care providers" under the Health Insurance Portability & Accountability Act
        ("Health Care Providers" and "HIPAA", respectively). Such Health Care Providers may
        require you to review and acknowledge their specific HIPAA Notice of Privacy Practices;
        any such terms are between you and such Health Care Provider. We encourage you to review
        our <Link to="/privacy">Privacy Policy</Link>, which provides additional information on
        how Pelvia may use your Personal Data (as defined in the Privacy Policy) and any
        communications between you and such Health Care Provider.
      </p>

      <h2>What are the basics of using Pelvia?</h2>
      <p>
        You may be required to sign up for an account, select a password and user name ("Pelvia
        User ID"), and provide us with certain information or data, such as your contact
        information. You promise to provide us with accurate, complete, and updated registration
        information about yourself. You may not select as your Pelvia User ID a name that you do
        not have the right to use, or another person's name with the intent to impersonate that
        person. You may not transfer your account to anyone else without our prior written
        permission.
      </p>

      <p>
        Additionally, you may be able to access certain parts or features of the Services by using
        your account credentials from other services (each, a "Third Party Account"), such as
        those offered by Google. By using the Services through a Third Party Account, you permit
        us to access certain information from such account for use by the Services. You are
        ultimately in control of how much information is accessible to us and may exercise such
        control by adjusting your privacy settings on your Third Party Account.
      </p>

      <p>
        You represent and warrant that you are an individual of legal age to form a binding
        contract (or if not, you've received your parent's or guardian's permission to use the
        Services and have gotten your parent or guardian to agree to these Terms on your behalf).
      </p>

      <p>
        You will only use the Services for your own internal, personal, non-commercial use, and
        not on behalf of or for the benefit of any third party, and only in a manner that complies
        with all laws that apply to you. If your use of the Services is prohibited by applicable
        laws, then you aren't authorized to use the Services. We can't and won't be responsible
        for your using the Services in a way that breaks the law.
      </p>

      <p>
        You will not share your Pelvia User ID, account or password with anyone, and you must
        protect the security of your Pelvia User ID, account, password and any other access tools
        or credentials. You're responsible for any activity associated with your Pelvia User ID
        and account.
      </p>

      <h2>Artificial Intelligence</h2>
      <p>
        The Services may generate certain content or output ("Output") through use of artificial
        intelligence. As such, you acknowledge and agree that (a) you will not include any
        unnecessary or deceptive User Submission (defined below) in connection with your use of
        the Services; (b) artificial intelligence and machine learning are rapidly evolving fields
        of study, and given the probabilistic nature of machine learning, use of the Services may
        in some situations result in incorrect, incomplete or inaccurate Output; (c) you must
        verify the accuracy and appropriateness of any Output before relying on any such Output;
        and (d) relying upon any Output without first verifying accuracy with a qualified medical
        professional could cause harm, including but not limited to legal, financial, and physical
        harm. You are responsible for any reliance on the accuracy, completeness, or usefulness of
        any Output. You should not act or refrain from acting on the basis of any information made
        available through the Services, including the Output. If the Output references any third
        party products or services, it does not mean the third party endorses or is affiliated
        with Pelvia.
      </p>

      <p>
        Pelvia cannot control and has no duty to take any action regarding how you may interpret,
        rely on or use any Output or what actions you may take as a result of having been exposed
        to Output. Under no circumstances will Pelvia be liable in any way for the Output,
        including, but not limited to, infringement of intellectual property rights, for any
        errors or omissions, or for any loss or damage of any kind incurred as a result of your
        interaction with the Output.
      </p>

      <h2>No Medical Advice; Not for Emergencies</h2>
      <p>
        Pelvia does not offer medical advice or diagnoses, or engage in the practice of medicine.
        Our Services are not intended to be a substitute for professional medical advice,
        diagnosis, or treatment and are offered for informational and communicative purposes only.
        The Services are not intended to be, and must not be taken to be, the practice of
        medicine, nursing, pharmacy or other healthcare advice by Pelvia.
      </p>

      <p>
        The Services are not meant to diagnose or treat any conditions. Only your medical
        professional can determine the right course of treatment for you and determine what is
        safe, appropriate and effective based on your needs. Reliance on any information provided
        by Pelvia or in connection with the Services is solely at your own risk. You are solely
        responsible for any decisions or actions you take based on the information and materials
        available through the Services.
      </p>

      <p>
        You acknowledge that although some Content (defined below) may be provided by individuals
        in the medical profession, the provision of such Content does not create a medical
        professional/patient relationship between you and Pelvia or between you and any other
        individual or entity, and does not constitute an opinion, medical advice, or diagnosis or
        treatment. Healthcare providers and patients should always obtain applicable diagnostic
        information from appropriate trusted sources. Healthcare providers should never withhold
        professional medical advice or delay in providing it because of something they have read
        in connection with our Services.
      </p>

      <div className="notice-box critical">
        <strong>THE SERVICES SHOULD NEVER BE USED AS A SUBSTITUTE FOR EMERGENCY CARE.</strong>
        IF YOU HAVE A MEDICAL OR MENTAL HEALTH EMERGENCY, ARE THINKING ABOUT SUICIDE OR TAKING
        ACTIONS THAT MAY CAUSE HARM TO YOU OR TO OTHERS, YOU SHOULD SEEK EMERGENCY TREATMENT AT
        THE NEAREST EMERGENCY ROOM OR DIAL 911.
      </div>

      <div className="notice-box critical">
        <p>
          IF YOU ARE USING Pelvia'S SERVICES BECAUSE YOU HAVE OR THINK YOU MAY HAVE AN ACUTE
          HEALTHCARE PROBLEM AND HAVE URGENT QUESTIONS, PLEASE CONTACT YOUR HEALTHCARE PROVIDER OR
          SEEK TREATMENT AT THE NEAREST EMERGENCY ROOM OR DIAL 911. THE SERVICES ARE NOT A
          REPLACEMENT FOR MEDICAL ADVICE AND CANNOT BE USED TO DETECT OR DIAGNOSE ANY MEDICAL
          ISSUES THAT YOU MAY BE EXPERIENCING.
        </p>
        <p>
          IF A HEALTHCARE PROVIDER HAS PROVIDED YOU WITH ACCESS TO THE SERVICES, YOU MUST CONTINUE
          TO DIRECT ALL URGENT QUESTIONS OR QUESTIONS ABOUT YOUR CARE PLAN TO YOUR HEALTH CARE
          PROVIDER.
        </p>
      </div>

      <div className="notice-box critical">
        <strong>CRISIS SERVICES DISCLAIMER</strong>
        <p>
          Pelvia is not a crisis service. If you are experiencing thoughts of self-harm or
          suicide, please contact the 988 Suicide and Crisis Lifeline by calling or texting 988.
          In an emergency, call 911. Pelvia monitors free text fields for safety using automated
          systems. Pelvia is not liable for delays in detection or response. By continuing you
          acknowledge that Pelvia is not a substitute for professional mental health care and that
          crisis support should be sought through qualified emergency services.
        </p>
      </div>

      <h2>What about messaging?</h2>
      <p>
        As part of the Services, you may receive communications through the Services, including
        messages that Pelvia sends you (for example, via email or SMS). When signing up for the
        Services, you will receive a welcome message and instructions on how to stop receiving
        messages. By signing up for the Services and providing us with your wireless number, you
        confirm that you want Pelvia to send you information regarding your account or
        transactions with us, which may include Pelvia using automated dialing technology to text
        you at the wireless number you provided, and you agree to receive communications from
        Pelvia, and you represent and warrant that each person you register for the Services or
        for whom you provide a wireless phone number has consented to receive communications from
        Pelvia. You agree to indemnify and hold Pelvia harmless from and against any and all
        claims, liabilities, damages (actual and consequential), losses and expenses (including
        attorneys' fees) arising from or in any way related to your breach of the foregoing.
      </p>

      <h2>Are there restrictions in how I can use the Services?</h2>
      <p>
        You represent, warrant, and agree that you will not provide or contribute anything,
        including any Content or User Submission (as those terms are defined below), to the
        Services, or otherwise use or interact with the Services, in a manner that:
      </p>

      <ol type="a">
        <li>
          infringes or violates the intellectual property rights or any other rights of anyone
          else (including Pelvia);
        </li>
        <li>
          violates any law or regulation, including, without limitation, any applicable export
          control laws, privacy laws or any other purpose not reasonably intended by Pelvia;
        </li>
        <li>
          is dangerous, harmful, fraudulent, deceptive, threatening, harassing, defamatory,
          obscene, or otherwise objectionable;
        </li>
        <li>
          jeopardizes the security of your Pelvia User ID, account or anyone else's (such as
          allowing someone else to log in to the Services as you);
        </li>
        <li>
          attempts, in any manner, to obtain the password, account, or other security information
          from any other user;
        </li>
        <li>
          violates the security of any computer network, or cracks any passwords or security
          encryption codes;
        </li>
        <li>
          runs Maillist, Listserv, any form of auto-responder or "spam" on the Services, or any
          processes that run or are activated while you are not logged into the Services, or that
          otherwise interfere with the proper working of the Services (including by placing an
          unreasonable load on the Services' infrastructure);
        </li>
        <li>
          "crawls," "scrapes," or "spiders" any page, data, or portion of or relating to the
          Services or Content (through use of manual or automated means);
        </li>
        <li>copies or stores any significant portion of the Content; or</li>
        <li>
          decompiles, reverse engineers, or otherwise attempts to obtain the source code or
          underlying ideas or information of or relating to the Services.
        </li>
      </ol>

      <p>A violation of any of the foregoing is grounds for termination of your right to use or access the Services.</p>

      <h2>What are my rights in the Services?</h2>
      <p>
        The materials displayed or performed or available on or through the Services, including,
        but not limited to, text, graphics, data, articles, photos, images, illustrations, User
        Submissions (as defined below) and so forth (all of the foregoing, the "Content") are
        protected by copyright and/or other intellectual property laws. You promise to abide by
        all copyright notices, trademark rules, information, and restrictions contained in any
        Content you access through the Services, and you won't use, copy, reproduce, modify,
        translate, publish, broadcast, transmit, distribute, perform, upload, display, license,
        sell, commercialize or otherwise exploit for any purpose any Content not owned by you, (i)
        without the prior consent of the owner of that Content or (ii) in a way that violates
        someone else's (including Pelvia's) rights.
      </p>

      <p>
        Subject to these Terms, we grant each user of the Services a worldwide, non-exclusive,
        non-sublicensable and non-transferable license to use (i.e., to download and display
        locally) Content solely for purposes of using the Services. Use, reproduction,
        modification, distribution or storage of any Content for any purpose other than using the
        Services is expressly prohibited without prior written permission from us. You understand
        that Pelvia owns the Services. You won't modify, publish, transmit, participate in the
        transfer or sale of, reproduce (except as expressly provided in this Section), create
        derivative works based on, or otherwise exploit any of the Services. The Services may
        allow you to copy or download certain Content, but please remember that even where these
        functionalities exist, all the restrictions in this section still apply.
      </p>

      <h2>
        What about anything I contribute to the Services? Do I have to grant any licenses to
        Pelvia or to other users?
      </h2>

      <h3>User Submissions</h3>
      <p>
        Anything you post, upload, share, store, or otherwise provide through the Services is your
        "User Submission." User Submissions include photos of your medicine labels, photos of your
        health insurance card, and photos of your insurance benefits documents. Some User
        Submissions may be viewable by other users. You are solely responsible for all User
        Submissions you contribute to the Services. You represent that all User Submissions
        submitted by you are accurate, complete, up-to-date, and in compliance with all applicable
        laws, rules and regulations.
      </p>

      <p>
        You agree that you will not post, upload, share, store, or otherwise provide through the
        Services any User Submissions that: (i) infringe any third party's copyrights or other
        rights (e.g., trademark, privacy rights, etc.); (ii) contain sexually explicit content or
        pornography; (iii) contain hateful, defamatory, or discriminatory content or incite hatred
        against any individual or group; (iv) exploit minors; (v) depict unlawful acts or extreme
        violence; (vi) depict animal cruelty or extreme violence towards animals; (vii) promote
        fraudulent schemes, multi-level marketing (MLM) schemes, get rich quick schemes, online
        gaming and gambling, cash gifting, work from home businesses, or any other dubious
        money-making ventures; or (viii) that violate any law.
      </p>

      <h3>Licenses</h3>
      <p>
        In order to display your User Submissions on the Services, and to allow other users to
        enjoy them (where applicable), you grant us certain rights in those User Submissions (see
        below for more information). Please note that all of the following licenses are subject to
        our <Link to="/privacy">Privacy Policy</Link> to the extent they relate to User
        Submissions that are also your personally-identifiable information.
      </p>

      <p>
        By submitting User Submissions through the Services, you hereby do and shall grant Pelvia
        a worldwide, non-exclusive, perpetual, royalty-free, fully paid, sublicensable and
        transferable license to use, edit, modify, truncate, aggregate, reproduce, distribute,
        prepare derivative works of, display, perform, and otherwise fully exploit the User
        Submissions in connection with this site, the Services and our (and our successors' and
        assigns') businesses, including without limitation for promoting and redistributing part
        or all of this site or the Services (and derivative works thereof) in any media formats
        and through any media channels (including, without limitation, third party websites and
        feeds), and including after your termination of your account or the Services. You also
        hereby do and shall grant each user of this site and/or the Services a non-exclusive,
        perpetual license to access your User Submissions through this site and/or the Services,
        and to use, edit, modify, reproduce, distribute, prepare derivative works of, display and
        perform such User Submissions, including after your termination of your account or the
        Services. For clarity, the foregoing license grants to us and our users do not affect your
        other ownership or license rights in your User Submissions, including the right to grant
        additional licenses to your User Submissions, unless otherwise agreed in writing. You
        represent and warrant that you have all rights to grant such licenses to us without
        infringement or violation of any third party rights, including without limitation, any
        privacy rights, publicity rights, copyrights, trademarks, contract rights, or any other
        intellectual property or proprietary rights.
      </p>

      <p>
        Certain features of the Services allow you to share information with others, including
        through your social networks or other Third Party Accounts. When Content is authorized for
        sharing, we will clearly identify the Content you are authorized to redistribute and the
        ways you may redistribute it, usually by providing a "share" button on or near the
        Content. If you share information from the Services with others through your Third Party
        Accounts, such as your social networks, you authorize Pelvia to share that information
        with the applicable Third Party Account provider. Please review the policies of any Third
        Party Account providers you share information with or through for additional information
        about how they may use your information. If you redistribute Content, you must be able to
        edit or delete any Content you redistribute, and you must edit or delete it promptly upon
        our request.
      </p>

      <p>
        Finally, you understand and agree that Pelvia, in performing the required technical steps
        to provide the Services to our users (including you), may need to make changes to your
        User Submissions to conform and adapt those User Submissions to the technical requirements
        of connection networks, devices, services, or media, and the foregoing licenses include
        the rights to do so.
      </p>

      <h2>Who is responsible for what I see and do on the Services?</h2>
      <p>
        Any information or Content publicly posted or privately transmitted through the Services
        is the sole responsibility of the person from whom such Content originated, and you access
        all such information and Content at your own risk, and we aren't liable for any errors or
        omissions in that information or Content or for any damages or loss you might suffer in
        connection with it. We cannot control and have no duty to take any action regarding how
        you may interpret and use the Content or what actions you may take as a result of having
        been exposed to the Content, and you hereby release us from all liability for you having
        acquired or not acquired Content through the Services. We can't guarantee the identity of
        any users with whom you interact in using the Services and are not responsible for which
        users gain access to the Services.
      </p>

      <p>
        You are responsible for all Content you contribute, in any manner, to the Services, and
        you represent and warrant you have all rights necessary to do so, in the manner in which
        you contribute it.
      </p>

      <p>
        The Services may contain links or connections to third-party websites or services that are
        not owned or controlled by Pelvia. When you access third-party websites or use third-party
        services, you accept that there are risks in doing so, and that Pelvia is not responsible
        for such risks.
      </p>

      <p>
        Pelvia has no control over, and assumes no responsibility for, the content, accuracy,
        privacy policies, or practices of or opinions expressed in any third-party websites or by
        any third party that you interact with through the Services. In addition, Pelvia will not
        and cannot monitor, verify, censor or edit the content of any third-party site or service.
        We encourage you to be aware when you leave the Services and to read the terms and
        conditions and privacy policy of each third-party website or service that you visit or
        utilize. By using the Services, you release and hold us harmless from any and all
        liability arising from your use of any third-party website or service.
      </p>

      <p>
        Your interactions with organizations and/or individuals found on or through the Services,
        including payment and delivery of goods or services, and any other terms, conditions,
        warranties or representations associated with such dealings, are solely between you and
        such organizations and/or individuals. You should make whatever investigation you feel
        necessary or appropriate before proceeding with any online or offline transaction with any
        of these third parties. You agree that Pelvia shall not be responsible or liable for any
        loss or damage of any sort incurred as the result of any such dealings.
      </p>

      <p>
        If there is a dispute between participants on this site or Services, or between users and
        any third party, you agree that Pelvia is under no obligation to become involved. In the
        event that you have a dispute with one or more other users, you release Pelvia, its
        directors, officers, employees, agents, and successors from claims, demands, and damages
        of every kind or nature, known or unknown, suspected or unsuspected, disclosed or
        undisclosed, arising out of or in any way related to such disputes and/or our Services.
        You shall and hereby do waive California Civil Code Section 1542 or any similar law of any
        jurisdiction, which says in substance: "A general release does not extend to claims that
        the creditor or releasing party does not know or suspect to exist in his or her favor at
        the time of executing the release and that, if known by him or her, would have materially
        affected his or her settlement with the debtor or released party."
      </p>

      <h2>Will Pelvia ever change the Services?</h2>
      <p>
        We're always trying to improve our Services, so they may change over time. We may suspend
        or discontinue any part of the Services, or we may introduce new features or impose limits
        on certain features or restrict access to parts or all of the Services. We'll try to give
        you notice when we make a material change to the Services that would adversely affect
        you, but this isn't always practical. We reserve the right to remove any Content from the
        Services at any time, for any reason (including, but not limited to, if someone alleges
        you contributed that Content in violation of these Terms), in our sole discretion, and
        without notice.
      </p>

      <h2>Do the Services cost anything?</h2>
      <p>
        The Services are currently free, but we reserve the right to charge for certain or all
        Services in the future. We will notify you before any Services you are then using begin
        carrying a fee, and if you wish to continue using such Services, you must pay all
        applicable fees for such Services.
      </p>

      <h2>What if I want to stop using the Services?</h2>
      <p>
        You're free to do that at any time by contacting us at{' '}
        <a href="mailto:support@pelvia.me">support@pelvia.me</a>; please refer to our{' '}
        <Link to="/privacy">Privacy Policy</Link>, as well as the licenses above, to understand
        how we treat information you provide to us after you have stopped using our Services.
      </p>

      <p>
        Pelvia is also free to terminate (or suspend access to) your use of the Services or your
        account for any reason in our discretion, including your breach of these Terms. Pelvia has
        the sole right to decide whether you are in violation of any of the restrictions set forth
        in these Terms.
      </p>

      <p>
        Account termination may result in destruction of any Content associated with your account,
        so keep that in mind before you decide to terminate your account.
      </p>

      <p>
        If you have deleted your account by mistake, contact us immediately at{' '}
        <a href="mailto:support@pelvia.me">support@pelvia.me</a>. We will try to help, but
        unfortunately, we can't promise that we can recover or restore anything.
      </p>

      <p>
        Provisions that, by their nature, should survive termination of these Terms shall survive
        termination. By way of example, all of the following will survive termination: any
        obligation you have to indemnify us, any limitations on our liability, any terms regarding
        ownership or intellectual property rights, and terms regarding disputes between us,
        including without limitation the arbitration agreement.
      </p>

      <h2>What about Mobile Applications?</h2>
      <p>
        You acknowledge and agree that the availability of our mobile application is dependent on
        the third party stores from which you download the application, e.g., the App Store from
        Apple or the Android app market from Google (each an "App Store"). Each App Store may
        have its own terms and conditions to which you must agree before downloading mobile
        applications from such store, including the specific terms relating to Apple App Store
        set forth below. You agree to comply with, and your license to use our application is
        conditioned upon your compliance with, such App Store terms and conditions. To the extent
        such other terms and conditions from such App Store are less restrictive than, or
        otherwise conflict with, the terms and conditions of these Terms of Use, the more
        restrictive or conflicting terms and conditions in these Terms of Use apply.
      </p>

      <h2>I use the Pelvia App available via the Apple App Store. Should I know anything about that?</h2>
      <p>
        <strong>
          Note: Pelvia does not currently offer an iOS application. These terms will apply when
          the application becomes available.
        </strong>
      </p>
      <p>
        These Terms apply to your use of all the Services, including our iOS applications (the
        "Application") available via the Apple, Inc. ("Apple") App Store, but the following
        additional terms also apply to the Application:
      </p>

      <ol type="a">
        <li>
          Both you and Pelvia acknowledge that the Terms are concluded between you and Pelvia
          only, and not with Apple, and that Apple is not responsible for the Application or the
          Content;
        </li>
        <li>
          The Application is licensed to you on a limited, non-exclusive, non-transferrable,
          non-sublicensable basis, solely to be used in connection with the Services for your
          private, personal, non-commercial use, subject to all the terms and conditions of these
          Terms as they are applicable to the Services;
        </li>
        <li>You will only use the Application in connection with an Apple device that you own or control;</li>
        <li>
          You acknowledge and agree that Apple has no obligation whatsoever to furnish any
          maintenance and support services with respect to the Application;
        </li>
        <li>
          In the event of any failure of the Application to conform to any applicable warranty,
          including those implied by law, you may notify Apple of such failure; upon notification,
          Apple's sole warranty obligation to you will be to refund to you the purchase price, if
          any, of the Application;
        </li>
        <li>
          You acknowledge and agree that Pelvia, and not Apple, is responsible for addressing any
          claims you or any third party may have in relation to the Application;
        </li>
        <li>
          You acknowledge and agree that, in the event of any third-party claim that the
          Application or your possession and use of the Application infringes that third party's
          intellectual property rights, Pelvia, and not Apple, will be responsible for the
          investigation, defense, settlement and discharge of any such infringement claim;
        </li>
        <li>
          You represent and warrant that you are not located in a country subject to a U.S.
          Government embargo, or that has been designated by the U.S. Government as a "terrorist
          supporting" country, and that you are not listed on any U.S. Government list of
          prohibited or restricted parties;
        </li>
        <li>
          Both you and Pelvia acknowledge and agree that, in your use of the Application, you will
          comply with any applicable third-party terms of agreement which may affect or be
          affected by such use; and
        </li>
        <li>
          Both you and Pelvia acknowledge and agree that Apple and Apple's subsidiaries are
          third-party beneficiaries of these Terms, and that upon your acceptance of these Terms,
          Apple will have the right (and will be deemed to have accepted the right) to enforce
          these Terms against you as the third-party beneficiary hereof.
        </li>
      </ol>

      <h2>What else do I need to know?</h2>

      <h3>Warranty Disclaimer</h3>
      <p>
        Pelvia and its licensors, suppliers, partners, parent, subsidiaries or affiliated
        entities, and each of their respective officers, directors, members, employees,
        consultants, contract employees, representatives and agents, and each of their respective
        successors and assigns (Pelvia and all such parties together, the "Pelvia Parties") make
        no representations or warranties concerning the Services, including without limitation
        regarding any Content or Output contained in or accessed through the Services, and the
        Pelvia Parties will not be responsible or liable for the accuracy, copyright compliance,
        legality, or decency of material contained in or accessed through the Services or any
        claims, actions, suits procedures, costs, expenses, damages or liabilities arising out of
        use of, or in any way related to your participation in, the Services. The Pelvia Parties
        make no representations or warranties regarding the Services, Content or Output, or
        suggestions or recommendations of services or products offered or purchased through or in
        connection with the Services. THE SERVICES, CONTENT AND OUTPUT ARE PROVIDED BY Pelvia (AND
        ITS LICENSORS AND SUPPLIERS) ON AN "AS-IS" BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER
        EXPRESS OR IMPLIED, INCLUDING, WITHOUT LIMITATION, IMPLIED WARRANTIES OF MERCHANTABILITY,
        FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR THAT USE OF THE SERVICES WILL BE
        UNINTERRUPTED OR ERROR-FREE, OR THAT USE OF THE SERVICES, CONTENT OR OUTPUT WILL MEET YOUR
        NEEDS. Pelvia WILL NOT BE RESPONSIBLE FOR ANY ACTIONS TAKEN BASED ON THE SERVICES, CONTENT
        OR OUTPUT. YOU ASSUME ALL RISKS ASSOCIATED WITH YOUR USE OF THE SERVICES, CONTENT AND
        OUTPUT. SOME STATES DO NOT ALLOW LIMITATIONS ON HOW LONG AN IMPLIED WARRANTY LASTS, SO THE
        ABOVE LIMITATIONS MAY NOT APPLY TO YOU.
      </p>

      <h3>Limitation of Liability</h3>
      <p>
        TO THE FULLEST EXTENT ALLOWED BY APPLICABLE LAW, UNDER NO CIRCUMSTANCES AND UNDER NO LEGAL
        THEORY (INCLUDING, WITHOUT LIMITATION, TORT, CONTRACT, STRICT LIABILITY, OR OTHERWISE)
        SHALL ANY OF THE Pelvia PARTIES BE LIABLE TO YOU OR TO ANY OTHER PERSON FOR (A) ANY
        INDIRECT, SPECIAL, INCIDENTAL, PUNITIVE OR CONSEQUENTIAL DAMAGES OF ANY KIND, INCLUDING
        DAMAGES FOR LOST PROFITS, BUSINESS INTERRUPTION, LOSS OF DATA, LOSS OF GOODWILL, WORK
        STOPPAGE, ACCURACY OF RESULTS, OR COMPUTER FAILURE OR MALFUNCTION, (B) ANY SUBSTITUTE
        GOODS, SERVICES OR TECHNOLOGY, (C) ANY AMOUNT, IN THE AGGREGATE, IN EXCESS OF ONE-HUNDRED
        ($100) DOLLARS OR (D) ANY MATTER BEYOND OUR REASONABLE CONTROL. SOME STATES DO NOT ALLOW
        THE EXCLUSION OR LIMITATION OF INCIDENTAL OR CONSEQUENTIAL OR CERTAIN OTHER DAMAGES, SO
        THE ABOVE LIMITATION AND EXCLUSIONS MAY NOT APPLY TO YOU.
      </p>

      <h3>Indemnity</h3>
      <p>
        You agree to indemnify and hold the Pelvia Parties harmless from and against any and all
        claims, liabilities, damages (actual and consequential), losses and expenses (including
        attorneys' fees) arising from or in any way related to any claims relating to (a) your use
        of the Services and Output (including, in each case, any actions taken by a third party
        using your account), and (b) your violation of these Terms. In the event of such a claim,
        suit, or action ("Claim"), we will attempt to provide notice of the Claim to the contact
        information we have for your account (provided that failure to deliver such notice shall
        not eliminate or reduce your indemnification obligations hereunder).
      </p>

      <h3>Assignment</h3>
      <p>
        You may not assign, delegate or transfer these Terms or your rights or obligations
        hereunder, or your Services account, in any way (by operation of law or otherwise)
        without Pelvia's prior written consent. We may transfer, assign, or delegate these Terms
        and our rights and obligations without consent.
      </p>

      <h3>Choice of Law</h3>
      <p>
        These Terms are governed by and will be construed under the Federal Arbitration Act,
        applicable federal law, and the laws of the State of Delaware, without regard to the
        conflicts of laws provisions thereof.
      </p>

      <h2>Arbitration Agreement</h2>
      <p>
        Please read the following ARBITRATION AGREEMENT carefully because it requires you to
        arbitrate certain disputes and claims with Pelvia and limits the manner in which you can
        seek relief from Pelvia. Both you and Pelvia acknowledge and agree that for the purposes
        of any dispute arising out of or relating to the subject matter of these Terms, Pelvia's
        officers, directors, employees and independent contractors ("Personnel") are third-party
        beneficiaries of these Terms, and that upon your acceptance of these Terms, Personnel will
        have the right (and will be deemed to have accepted the right) to enforce these Terms
        against you as the third-party beneficiary hereof.
      </p>

      <h3>(a) Arbitration Rules; Applicability of Arbitration Agreement</h3>
      <p>
        The parties shall use their best efforts to settle any dispute, claim, question, or
        disagreement arising out of or relating to the subject matter of these Terms directly
        through good-faith negotiations, which shall be a precondition to either party initiating
        arbitration. If such negotiations do not resolve the dispute, it shall be finally settled
        by binding arbitration in New York County, New York. The arbitration will proceed in the
        English language, in accordance with the JAMS Streamlined Arbitration Rules and Procedures
        (the "Rules") then in effect, by one commercial arbitrator with substantial experience in
        resolving intellectual property and commercial contract disputes. The arbitrator shall be
        selected from the appropriate list of JAMS arbitrators in accordance with such Rules.
        Judgment upon the award rendered by such arbitrator may be entered in any court of
        competent jurisdiction.
      </p>

      <h3>(b) Costs of Arbitration</h3>
      <p>
        The Rules will govern payment of all arbitration fees. Pelvia will pay all arbitration
        fees for claims less than seventy-five thousand ($75,000) dollars. Pelvia will not seek
        its attorneys' fees and costs in arbitration unless the arbitrator determines that your
        claim is frivolous.
      </p>

      <h3>(c) Small Claims Court; Infringement</h3>
      <p>
        Either you or Pelvia may assert claims, if they qualify, in small claims court in New York
        County, New York or any United States county where you live or work. Furthermore,
        notwithstanding the foregoing obligation to arbitrate disputes, each party shall have the
        right to pursue injunctive or other equitable relief at any time, from any court of
        competent jurisdiction, to prevent the actual or threatened infringement, misappropriation
        or violation of a party's copyrights, trademarks, trade secrets, patents or other
        intellectual property rights.
      </p>

      <h3>(d) Waiver of Jury Trial</h3>
      <p>
        YOU AND Pelvia WAIVE ANY CONSTITUTIONAL AND STATUTORY RIGHTS TO GO TO COURT AND HAVE A
        TRIAL IN FRONT OF A JUDGE OR JURY. You and Pelvia are instead choosing to have claims and
        disputes resolved by arbitration. Arbitration procedures are typically more limited, more
        efficient, and less costly than rules applicable in court and are subject to very limited
        review by a court. In any litigation between you and Pelvia over whether to vacate or
        enforce an arbitration award, YOU AND Pelvia WAIVE ALL RIGHTS TO A JURY TRIAL, and elect
        instead to have the dispute be resolved by a judge.
      </p>

      <h3>(e) Waiver of Class or Consolidated Actions</h3>
      <p>
        ALL CLAIMS AND DISPUTES WITHIN THE SCOPE OF THIS ARBITRATION AGREEMENT MUST BE ARBITRATED
        OR LITIGATED ON AN INDIVIDUAL BASIS AND NOT ON A CLASS BASIS. CLAIMS OF MORE THAN ONE
        CUSTOMER OR USER CANNOT BE ARBITRATED OR LITIGATED JOINTLY OR CONSOLIDATED WITH THOSE OF
        ANY OTHER CUSTOMER OR USER. If however, this waiver of class or consolidated actions is
        deemed invalid or unenforceable, neither you nor Pelvia is entitled to arbitration;
        instead all claims and disputes will be resolved in a court as set forth in (g) below.
      </p>

      <h3>(f) Opt-out</h3>
      <p>
        You have the right to opt out of the provisions of this Section by sending written notice
        of your decision to opt out to support@pelvia.me. You must include (i) your name and
        residence address, (ii) the email address and/or telephone number associated with your
        account, and (iii) a clear statement that you want to opt out of these Terms' arbitration
        agreement.
      </p>

      <h3>(g) Exclusive Venue</h3>
      <p>
        If you send the opt-out notice in (f), and/or in any circumstances where the foregoing
        arbitration agreement permits either you or Pelvia to litigate any dispute arising out of
        or relating to the subject matter of these Terms in court, then the foregoing arbitration
        agreement will not apply to either party, and both you and Pelvia agree that any judicial
        proceeding (other than small claims actions) will be brought in the state or federal
        courts located in, respectively, New York County, New York, or the federal district in
        which that county falls.
      </p>

      <h3>(h) Severability</h3>
      <p>
        If the prohibition against class actions and other claims brought on behalf of third
        parties contained above is found to be unenforceable, then all of the preceding language
        in this Arbitration Agreement section will be null and void. This arbitration agreement
        will survive the termination of your relationship with Pelvia.
      </p>

      <h2>Miscellaneous</h2>
      <p>
        You will be responsible for paying, withholding, filing, and reporting all taxes, duties,
        and other governmental assessments associated with your activity in connection with the
        Services, provided that the Pelvia may, in its sole discretion, do any of the foregoing on
        your behalf or for itself as it sees fit. The failure of either you or us to exercise, in
        any way, any right herein shall not be deemed a waiver of any further rights hereunder. If
        any provision of these Terms are found to be unenforceable or invalid, that provision will
        be limited or eliminated, to the minimum extent necessary, so that these Terms shall
        otherwise remain in full force and effect and enforceable. You and Pelvia agree that these
        Terms are the complete and exclusive statement of the mutual understanding between you and
        Pelvia, and that these Terms supersede and cancel all previous written and oral
        agreements, communications and other understandings relating to the subject matter of
        these Terms. You hereby acknowledge and agree that you are not an employee, agent,
        partner, or joint venture of Pelvia, and you do not have any authority of any kind to bind
        Pelvia in any respect whatsoever.
      </p>

      <p>
        Except as expressly set forth in the sections above regarding the Apple Application and
        the arbitration agreement, you and Pelvia agree there are no third-party beneficiaries
        intended under these Terms.
      </p>
    </LegalLayout>
  )
}
